/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Paper,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ApiIcon from '@mui/icons-material/Http';
import SaveIcon from '@mui/icons-material/Save';
import DispatchIcon from '@mui/icons-material/LocalShipping';
import CompleteIcon from '@mui/icons-material/CheckCircleOutline';
import RejectIcon from '@mui/icons-material/CancelOutlined';
import DefaultIcon from '@mui/icons-material/Widgets';
import { useFormContext } from 'react-hook-form';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import RuleConditions from '@product-framework/components/RuleConditions';

const iconMap = {
  ServiceJob: <ApiIcon />,
  Save: <SaveIcon />,
  Dispatch: <DispatchIcon />,
  JOB_COMPLETE_BUTTON: <CompleteIcon />,
  Reject: <RejectIcon />,
};

function getButtonIcon(button) {
  // Prefer icon field, fallback to type/label heuristics
  if (button.icon && iconMap[button.icon]) return iconMap[button.icon];
  const label = (button.label && button.label.defaultMessage) || '';
  if (/save/i.test(label) || button.icon === 'Save') return <SaveIcon />;
  if (/dispatch/i.test(label) || button.icon === 'Dispatch')
    return <DispatchIcon />;
  if (/complete/i.test(label) || /complete/i.test(button.type))
    return <CompleteIcon />;
  return <DefaultIcon />;
}

function renderPreFlight(pre) {
  if (!pre || !pre.length) return <Typography variant="body2">—</Typography>;
  return (
    <Stack spacing={1}>
      {pre.map((p, i) => (
        <Paper variant="outlined" sx={{ p: 1 }} key={i}>
          <Typography variant="subtitle2">{p.type}</Typography>
          {p.params && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {Array.isArray(p.params)
                ? p.params.join(', ')
                : JSON.stringify(p.params)}
            </Typography>
          )}
        </Paper>
      ))}
    </Stack>
  );
}

function ToggleElementById({ targetId, label = 'Toggle' }) {
  const [visible, setVisible] = useState(false);

  const toggle = () => {
    const element = document.getElementById(targetId);
    if (element) {
      element.style.display = visible ? 'none' : 'block';
      setVisible(!visible);
    }
  };

  return (
    <Button variant="text" onClick={toggle}>
      {visible ? `Hide ${label}` : `Show ${label}`}
    </Button>
  );
}

function FormViewer({ props }) {
  if (props?.show !== true) {
    return null;
  }
  const { getValues } = useFormContext();
  const data = getValues();

  // Accept either passed data prop or fall back to sample (user should pass real JSON)
  const json = data?.formUiSchema ? JSON.parse(data?.formUiSchema) : null;
  if (!json) {
    return 'Nothing to render';
  }

  const buttons = json.BUTTONS || [];
  const sections = json.PAGESECTIONS || [];

  return (
    <Box sx={{ px: 2 }}>
      <Typography variant="h6" sx={{ mt: 4 }}>
        Page Sections
      </Typography>

      <Box sx={{ mt: 1 }}>
        {sections.map((sec, sidx) => {
          const title =
            sec.locale?.defaultMessage ||
            sec.locale?.id ||
            `Section ${sidx + 1}`;
          const children = sec.children || [];
          return (
            <Accordion key={sidx}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">{title}</Typography>
                {sec.visibility && (
                  <Chip label="conditional" size="small" sx={{ ml: 2 }} />
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Grid2 container spacing={2}>
                  {children.map((c, cidx) => {
                    // Render subsections (subsection control) or simple controls
                    if (
                      c.control === 'subsection' &&
                      Array.isArray(c.children)
                    ) {
                      return (
                        <Grid2 item xs={12} key={cidx}>
                          <Card variant="outlined" sx={{ p: 1 }}>
                            <Typography variant="subtitle2">
                              {c.label || c.name}
                            </Typography>
                            <Grid2 container spacing={1} sx={{ mt: 1 }}>
                              {c.children.map((sub, si) => (
                                <Grid2 item xs={12} sm={6} md={4} key={si}>
                                  <Paper variant="outlined" sx={{ p: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 700 }}>
                                      {sub.label || sub.name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      display="block">
                                      control: {sub.control}
                                    </Typography>
                                    {sub.required && (
                                      <Chip
                                        label="required"
                                        size="small"
                                        sx={{ mt: 0.5 }}
                                      />
                                    )}
                                    {sub.validations && (
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        sx={{ mt: 0.5 }}>
                                        validations:{' '}
                                        {JSON.stringify(sub.validations)}
                                      </Typography>
                                    )}
                                    {sub.dependencies && (
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        sx={{ mt: 0.5 }}>
                                        dependencies:{' '}
                                        {JSON.stringify(sub.dependencies)}
                                      </Typography>
                                    )}
                                  </Paper>
                                </Grid2>
                              ))}
                            </Grid2>
                          </Card>
                        </Grid2>
                      );
                    }

                    // normal control
                    return (
                      <Grid2 item xs={12} sm={6} md={4} key={cidx}>
                        <Paper variant="outlined" sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {c.label || c.name}
                          </Typography>
                          <Typography variant="caption" display="block">
                            control: {c.control}
                          </Typography>
                          {c.required && (
                            <Chip
                              label="required"
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                          {c.validations && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ mt: 0.5 }}>
                              validations: {JSON.stringify(c.validations)}
                            </Typography>
                          )}
                          {c.permission && (
                            <Chip
                              label={`perm: ${c.permission}`}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Paper>
                      </Grid2>
                    );
                  })}
                </Grid2>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      <Typography variant="h6" sx={{ mt: 2 }}>
        Buttons
      </Typography>

      <Grid2 container spacing={2} sx={{ mt: 1 }}>
        {buttons.map((btn, idx) => (
          <Accordion key={idx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {(btn.label && btn.label.defaultMessage) || 'Unnamed'}
              </Typography>
              {btn.visibility && (
                <Chip label="conditional" size="small" sx={{ ml: 2 }} />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Grid2 item key={idx}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  justifyContent="space-between">
                  <Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title={btn.icon || 'icon'}>
                        <IconButton size="small">
                          {getButtonIcon(btn)}
                        </IconButton>
                      </Tooltip>
                      <Typography variant="h6">
                        {(btn.label && btn.label.defaultMessage) || 'Unnamed'}
                      </Typography>
                      <Chip label={btn.type} size="small" sx={{ ml: 1 }} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Dialog: {btn.dialogType || '—'}
                    </Typography>
                  </Stack>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">
                  Visibility <ToggleElementById targetId={`button${idx}Rule`} />
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {btn?.visibility &&
                    Array.isArray(btn?.visibility?.rules) &&
                    btn?.visibility?.rules.length > 0 &&
                    btn?.visibility?.rules.map(r => (
                      <RuleConditions conditions={r.conditions} />
                    ))}
                </Typography>
                <Typography
                  id={`button${idx}Rule`}
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap' }}>
                  <code>{JSON.stringify(btn?.visibility, 0, 2)}</code>
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">APIs</Typography>
                {btn.api && btn.api.length ? (
                  <Table size="small" aria-label="api table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Method</TableCell>
                        <TableCell>URL / Endpoint</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {btn.api.map((a, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ textTransform: 'uppercase' }}>
                            {a.method}
                          </TableCell>
                          <TableCell>{a.url}</TableCell>
                          <TableCell>
                            {a.successMessage
                              ? a.successMessage.defaultMessage
                              : a.errorMessage
                                ? a.errorMessage.defaultMessage
                                : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2">No API configured</Typography>
                )}
                {buttons[idx].api &&
                  buttons[idx].api.some(
                    a => a.preFlightHook && a.preFlightHook.length,
                  ) && (
                    <>
                      <Divider sx={{ mt: 1 }} />
                      <Typography variant="subtitle2">
                        Pre-flight hooks
                      </Typography>
                      {buttons[idx].api.map((a, i) => (
                        <Box key={i} sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Endpoint: {a.url}
                          </Typography>
                          {renderPreFlight(a.preFlightHook)}
                        </Box>
                      ))}
                    </>
                  )}
              </Grid2>
            </AccordionDetails>
          </Accordion>
        ))}
      </Grid2>
    </Box>
  );
}

export default FormViewer;
